<template>
    <section name="CreateEditNotice">
        <form-modal
            :title="title"
            v-model="modelValue"
            :submit-button="'Submit'"
            @submit="submitForm">
            <section class="vstack gap-3">
                <div
                    class="mb-3"
                    v-if="dcraErrors && dcraErrors.length > 0">
                    <div
                        class="alert alert-danger fade show"
                        role="alert">                        
                        <p>Error(s):</p>
                        <ul class="list-group">
                            <li class="ms-3" style="list-style-type: disc" v-for="error in dcraErrors">
                                {{ error }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div>
                    <label for="agency">Agency *</label>
                    <b-agency
                        name="agency"
                        v-model="form.agency"
                        :can-clear="false"
                        :error="errors.agency"
                        :select-first="true"></b-agency>
                </div>
                <div>
                    <label for="lastName">Last Name *</label>
                    <b-input
                        name="lastName"
                        type="text"
                        :error="errors.lastName"
                        required="required"
                        v-model="form.lastName" />
                </div>
                <div>
                    <label for="firstName">First Name *</label>
                    <b-input
                        name="firstName"
                        type="text"
                        :error="errors.firstName"
                        required="required"
                        v-model="form.firstName" />
                </div>
                <div>
                    <label for="middleName">Middle Name</label>
                    <b-input
                        name="middleName"
                        type="text"
                        :error="errors.middleName"
                        v-model="form.middleName" />
                </div>
                <div>
                    <label for="sex">Sex *</label>
                    <SelectField
                        class="w-100"
                        v-model="form.sex"
                        :select-items="dcraStore.getSex"
                        mode="single"
                        :error="errors.sex"
                        :min="1"></SelectField>
                </div>
                <div>
                    <label for="race">Race *</label>
                    <SelectField
                        class="w-100"
                        v-model="form.race"
                        :select-items="dcraStore.getRace"
                        mode="multiple"
                        :error="errors.race"
                        :min="1"
                        :include-show-all-option="false"
                        :hide-selected="false"
                        :close-on-select="false"></SelectField>
                </div>
                <div>
                    <label for="ethnicity">Ethnicity *</label>
                    <SelectField
                        class="w-100"
                        v-model="form.ethnicity"
                        :select-items="dcraStore.getEthnicity"
                        mode="single"
                        :error="errors.ethnicity"
                        :min="1"></SelectField>
                </div>
                <div>
                    <label for="birthYear">Birth Year *</label>
                    <b-input
                        name="birthYear"
                        type="number"
                        :error="errors.birthYear"
                        required="required"
                        v-model="form.birthYear"
                        :min="new Date().getFullYear() - 123"
                        :placeholder="'YYYY or 9999 for unknown'" />
                    <!-- The oldest person ever verified was Jeanne Calment from France, who lived to be 122 years and 164 days old. 
                     So we round this number up to 123 to set the min value.  -->
                </div>
                <div>
                    <label for="dateOfDeath">Date and Time of Death *</label>
                    <b-date-picker
                        v-model="form.dateOfDeath"
                        :error="errors.dateOfDeath"
                        :month-picker="false"
                        :auto-apply="false"
                        :enable-time-picker="true"
                        :enable-minutes="true"
                        :enable-seconds="false"
                        :max-date="new Date()"
                        :min-date="new Date(props.startDate)"
                        :auto-position="true"></b-date-picker>
                </div>
                <div>
                    <label for="eventLocationType">Event Location Type *</label>
                    <SelectField
                        class="w-100"
                        v-model="form.eventLocationType"
                        :select-items="dcraStore.getEventLocationType"
                        mode="single"
                        :error="errors.eventLocationType"
                        :min="1"></SelectField>
                </div>
                <fieldset class="bg-light">
                    <legend class="fs-5">Location of Death</legend>
                    <div class="pb-2">
                        <label for="locationName">Location Name</label>
                        <b-input
                            name="locationName"
                            type="text"
                            :error="errors.locationName"
                            v-model="form.locationName" />
                    </div>
                    <div class="pb-2">
                        <label for="streetAddress">Street Address *</label>
                        <b-input
                            name="streetAddress"
                            type="text"
                            :error="errors.streetAddress"
                            required="required"
                            v-model="form.streetAddress" />
                    </div>
                    <div class="pb-2">
                        <label for="city">City *</label>
                        <b-input
                            name="city"
                            type="text"
                            :error="errors.city"
                            required="required"
                            v-model="form.city" />
                    </div>
                    <div class="pb-2">
                        <label for="state">State *</label>
                        <SelectField
                            class="w-100"
                            v-model="form.state"
                            :select-items="dcraStore.getState"
                            mode="single"
                            :error="errors.state"
                            :min="1"></SelectField>
                    </div>
                    <div class="pb-2">
                        <label for="zipCode">Zip Code *</label>
                        <b-input
                            name="zipCode"
                            type="text"
                            :error="errors.zipCode"
                            required="required"
                            v-model="form.zipCode"
                            :maxlength="10" />
                    </div>
                </fieldset>
                <div>
                    <label for="mannerOfDeath">Manner of Death *</label>
                    <SelectField
                        class="w-100"
                        v-model="form.mannerOfDeath"
                        :select-items="dcraStore.getMannerOfDeath"
                        mode="single"
                        :error="errors.mannerOfDeath"
                        :min="1"></SelectField>
                </div>
                <div v-if="form.mannerOfDeath === 'I'">
                    <label for="investigatingAgency">Agency investigating death and expected completion date</label>
                    <b-textarea
                        name="investigatingAgency"                        
                        :error="errors.investigatingAgency"                        
                        v-model="form.investigatingAgency"
                        :max-length="500" />
                </div>
                <div v-if="form.mannerOfDeath === 'O'">
                    <label for="details">Details</label>
                    <b-textarea
                        name="details"
                        aria-required="false"
                        :error="errors.details"
                        v-model="form.details" />
                </div>
                <div>
                    <label for="eventDescription">Event Description *</label>
                    <b-textarea
                        name="eventDescription"
                        aria-required="true"
                        required="required"
                        :error="errors.eventDescription"
                        v-model="form.eventDescription" />
                </div>
            </section>
        </form-modal>
    </section>
</template>

<script setup lang="ts">
import {ref, computed, defineProps, watch, onMounted} from "vue";
import {Death, DeathForm} from "./schema";
import {useDcraStore} from "@/stores/dcraStore";
import FormModal from "../Misc/FormModal.vue";
import SelectField from "@/components/Misc/SelectField.vue";
import {BInput, BTextarea, BAgency, BDatePicker} from "@/components/Fields";
import {array, date, number, object, string, ValidationError} from "yup";
import {useFormValidation} from "@/validation";

const modelValue = defineModel({type: Boolean});
const props = defineProps<{
    death: Death;
    startDate: String;
}>();
const emit = defineEmits(["dismiss", "submit", "update:modelValue"]);

var dcraStore = useDcraStore();

const {handleSubmit, errors, resetValidation} = useFormValidation();

const form = ref({
    agency: "",
    lastName: "",
    firstName: "",
    middleName: "",
    sex: "",
    race: [],
    ethnicity: "",
    birthYear: "",
    dateOfDeath: undefined,
    locationName: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    eventLocationType: "",
    mannerOfDeath: "",
    investigatingAgency: "",
    details: "",
    eventDescription: "",
} as DeathForm);

const schema = object<DeathForm>({
    agency: string().required("Agency is a required field"),
    lastName: string()
        .required("Last name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    firstName: string()
        .required("First name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    middleName: string().matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    sex: string().required("Sex is a required field"),
    race: array().required("Race is a required field").min(1),
    ethnicity: string().required("Ethnicity is a required field"),
    birthYear: number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(new Date().getFullYear() - 123)
        .required(),
    dateOfDeath: date().required("Date of death is a required field"),
    locationName: string().matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    streetAddress: string()
        .required("Street address is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    city: string()
        .required("City is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    state: string().required().max(2),
    zipCode: string()
        .matches(/^\d{5}(-\d{4})?$/, "Zipcode must be in the format 12345 or 12345-6789")
        .required("Zipcode is a required field")
        .max(10),
    eventLocationType: string().required("Event location is a required field"),
    mannerOfDeath: string().required("Manner of death is a required field"),
    investigatingAgency: string().max(500),
    details: string().matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters").max(500),
    eventDescription: string().required("Event Description is a required field").max(500),
});

var submitDisabled = ref(false);
var dcraErrors = ref([] as string[]);
//Computed
const title = computed(() => (props.death?.id > 0 ? "Edit Death" : "Add Death"));

//FUNctions
const submitForm = async () => {
    if (await handleSubmit(schema, form.value)) {
        if (props.death?.id > 0) {
            await dcraStore.editDeath(props.death.id, form.value);
        } else {
            await dcraStore.createDeath(form.value);
        }

        window.location.href = "dataupload.aspx?source=8";
    }
};

const setFormValues = async () => {
    resetValidation();

    if (props.death.id > 0) {
        var response = await dcraStore.fetchDeath(props.death.id);

        if (response.status == 200) {
            let deathForm = response.data;
            form.value = deathForm;
        }
    } else {
        form.value = {
            agency: "",
            lastName: "",
            firstName: "",
            middleName: "",
            sex: "",
            race: [],
            ethnicity: "",
            birthYear: "",
            dateOfDeath: undefined,
            locationName: "",
            streetAddress: "",
            city: "",
            state: "",
            zipCode: "",
            eventLocationType: "",
            mannerOfDeath: "",
            investigatingAgency: "",
            details: "",
            eventDescription: "",
        } as DeathForm;
    }
};

const getItemErrors = async () => {
    dcraErrors.value = [] as string[];

    if (props.death?.id > 0) {
        let response = await dcraStore.getDeathErrors(props.death.id);

        if (response != null && response.status == 200) {
            dcraErrors.value = response.data ?? ([] as string[]);
        }
    }
};
//LifeCycle Hooks
onMounted(async () => {
    await dcraStore.fetchCodeLabelByKind();
    await setFormValues();
    await getItemErrors();
});

//Watches
watch(
    () => props.death,
    async () => {
        await setFormValues();
        await getItemErrors();
    }
);

watch(
    () => form.value,
    async () => {
        try {
            if (errors.value.length == 0) {
                return;
            }

            errors.value = Array.isArray(errors.value) ? [] : {};
            await schema.validate(form.value, {abortEarly: false});
        } catch (err) {
            const error = err as ValidationError;
            if (error.name !== "ValidationError") throw err;

            // Build an object map of field -> message
            const newErrors: Record<string, string> = {};
            error.inner.forEach((it: ValidationError) => {
                if (it.path) newErrors[it.path] = it.message;
            });
            errors.value = newErrors;
        }
    },
    {deep: true}
);
</script>

<style scoped>
.dcra-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
fieldset {
    border: 1px solid #ccc;
    padding: 1rem;
}
legend {
    font-weight: bold;
}
label {
    display: block;
    margin-bottom: 0.25rem;
}
input,
select,
textarea {
    width: 100%;
    padding: 0.25rem;
    box-sizing: border-box;
}
button {
    align-self: flex-start;
    padding: 0.5rem 1rem;
}
</style>
